import { Translate, Options } from 'translate.js';
import type { messages } from '../services';
import { uniqueId } from 'mithril-materialized';
import { LLMConfig } from '../components/ui/llm';
import type { Languages } from '../services/translations';
import { loadStarterKit } from './starter-kit';
import { thresholdColors } from './usage-legend';

export { thresholdColors } from './usage-legend';

export type DataModel = {
  version?: number;
  lastUpdate?: number;
  /** Current scenario */
  scenario: Scenario;
  /** List of other possible scenarios, excluding the current scenario */
  scenarios: Scenario[];
  personas?: Persona[];
};

export type Persona = Item & {
  url?: string;
  /** Fingerprint of the starter-kit version, used to detect user edits */
  starterHash?: string;
};

export type OldDataModel = {
  scenarios: {
    current: {
      id: string;
      type: string;
      category: string;
      name: string;
      desc: string;
      categories: {
        [key: string]: string[];
      };
      inconsistencies: Inconsistency[];
      narratives: Array<{
        id: string;
        name: string;
        components: { [key: ID]: ID };
        narrative: string;
        included: boolean;
      }>;
    };
  };
} & {
  [key: string]: {
    list: Array<{
      id: string;
      name: string;
      type: string;
      desc?: string;
      context?: {
        type: 'LOCATION' | 'LOCATIONTYPE';
        data: {
          COORDINATES?: string;
          NAME?: string;
          [key: string]: string | undefined;
        };
      };
    }>;
  };
};

export type InconsistencyType = 'partly' | 'totally';

/** Old inconsistency type */
export type Inconsistency = {
  ids: [from: string, to: string];
  type: InconsistencyType;
};

/**
 * New inconsistency type, where a missing value indicates a combination that
 * is possible, a value of true indicates the combination is not possible, and
 * false indicates it is improbable.
 */
export type Inconsistencies = {
  [rowOrColId: string]: { [rowOrColId: string]: boolean };
};

export type Item = {
  id: ID;
  label: string;
  /** Description of the item, may use markdown */
  desc?: string;
};

export const contextTypeOptions = (
  t: Translate<typeof messages, Options>
): Array<{ id: ContextType; label: string }> => [
  { id: 'none', label: t('NONE') },
  { id: 'location', label: t('LOCATION') },
  { id: 'locationType', label: t('LOCATION_TYPE') },
];

export type ContextType = 'none' | 'location' | 'locationType';
export type LocationType = 'name' | 'coords' | 'circle' | 'ellipse' | 'osm';
export type LocationTypeType = 'list' | 'keyValue';

export type ContextualItem = Item & {
  context?: ContextType;
  /** Location name, e.g. the name of a city or landmark */
  location?: string;
  /** Type of location when the context is location, e.g. name or coordinates */
  locationType?: LocationType;
  /** Type of location when the context is locationType, e.g. pick from a default list or OSM key value */
  locationTypeType?: LocationTypeType;
  /** Location's latitude, WGS84 */
  lat?: number;
  /** Location's longitude, WGS84 */
  lon?: number;
  /** Radius in meters, for circles (deprecated, use radii) */
  radius?: number;
  /** Comma-separated list of radii in meters */
  radii?: string;
  /** OSM type */
  osmTypeId?: string;
  /** OSM attribute key */
  key?: string;
  /** OSM attribute value */
  value?: string;
};

/** An alternative set of decisions (measures) for the situation of a narrative */
export type CourseOfAction = {
  id: ID;
  label: string;
  desc?: string;
  /** Decision componentID => selected valueIds */
  components: { [key: ID]: ID[] };
  notes?: string;
  /** Reflection on the decisions (LLM decision prompt or typed), editable */
  reflection?: string;
  /** Residual risk after the course of action */
  risk?: ID;
  probability?: ID;
  impact?: ID;
  /** Optional criteria scores (1 poor - 5 good), keyed by criterion, e.g. `effect` */
  scores?: { [criterion: string]: number };
};

export type Narrative = Item & {
  /** componentID => a component's valueId. Holds the situation; decisions live in `coas`. */
  components: { [key: ID]: ID[] };
  /** Courses of action: alternative decisions for this situation */
  coas?: CourseOfAction[];
  /** Id of the course of action that is being edited and used in prompts and comparisons */
  activeCoaId?: ID;
  /** Is the narrative included in the final set of narratives or a temporary scenario, just in case */
  included: boolean;
  /** Is the narrative saved in the set of narratives (so we should be able to delete or replace it) */
  saved: boolean;
  /** Name of the generated batch (covering set) this narrative belongs to, if any */
  batch?: string;
  /** Risk that the narrative occurs = chance x impact */
  risk?: ID;
  /** Probability that the narrative occurs */
  probability?: ID;
  /** Impact of the narrative */
  impact?: ID;
  /** Effect on personas */
  personaEffects: { [personaId: ID]: { scale?: number; story?: string } };
};

/** HEX color code */
export type Color = string;

/** Threshold value and the corresponding color */
export type ThresholdColor = { threshold: number; color: Color };

export type Scenario = Item & {
  /** Version of the scenario data format, used for migrations (missing = 1) */
  dataVersion?: number;
  /** ISO 639-1 code of the language the box is written in */
  language?: string;
  /** Template string to convert the scenario items to a fluid text. */
  template?: string;
  /** Optional LLM configuration */
  llm?: LLMConfig;
  /** If true, do not show inconsistent combinations between components */
  hideInconsistentValues: boolean;
  /** If true, activate the decision support module */
  includeDecisionSupport: boolean;
  /** If true, the user has been asked whether to turn on decision support for this scenario */
  decisionSupportPrompted?: boolean;
  /** If true, activate the map support */
  includeMapSupport?: boolean;
  /** Default map configuration */
  mapConfig?: {
    lat: number;
    lon: number;
    zoom: number;
    height?: number;
  };
  /** Map units */
  mapUnits?: 'metric' | 'imperial';
  /** Selected OSM amenities to display */
  osmAmenities?: string[];
  /** Combinations of scenario components that should not be used together */
  inconsistencies: Inconsistencies;
  /** Categories of components */
  categories: Category[];
  /** Scenario components, also known as key factors and key values */
  components: ScenarioComponent[];
  /** Stories consisting of scenario components and a narrative */
  narratives: Narrative[];
  /** Color thresholds to indicate how often a scenario component is used */
  thresholdColors: ThresholdColor[];
  // components: ScenarioComponent[];
  /** Selected personas, relevant for current scenario */
  personas?: ID[];
  /** Optional weights (0-5) of the criteria used to score courses of action; missing = 1 */
  criteriaWeights?: { [criterion: string]: number };
  /** Decisions taken on the decision-support page, with rationale and timestamp */
  decisionLog?: DecisionLogEntry[];
  /** Fingerprint of the starter-kit version, used to detect user edits */
  starterHash?: string;
};

/** A recorded decision; scenario and COA labels are stored so removed references stay readable */
export type DecisionLogEntry = {
  id: ID;
  /** Time the decision was recorded (ms since epoch) */
  timestamp: number;
  /** Time of the last edit (ms since epoch) */
  edited?: number;
  narrativeId?: ID;
  narrativeLabel?: string;
  coaId?: ID;
  coaLabel?: string;
  decision: string;
  rationale?: string;
  author?: string;
};

/** Category of components, e.g. to separate context from narrative */
export type Category = Item & {
  /** If true, the category is used for decision support */
  decisionSupport?: boolean;
  componentIds?: ID[];
};

/** Key factors and their values that make up a narrative */
export type ScenarioComponent = Item & {
  /** Optional sort order */
  order?: number;
  /** Manual mode - if so, do not automatically generate a value for it */
  manual?: boolean;
  /** Key factor values */
  values?: ContextualItem[];
  /** Are there any contexts that are relevant, such as a location or mitigation measures */
  contexts?: ContextType[];
};

export const newScenario = (label = '') => ({
  id: uniqueId(),
  label,
  desc: '',
  includeDecisionSupport: false,
  hideInconsistentValues: false,
  inconsistencies: {} as Inconsistencies,
  categories: [],
  components: [],
  narratives: [],
  thresholdColors,
});

export const emptyModel = (label = '') =>
  ({
    version: 1,
    lastUpdate: new Date().valueOf(),
    scenarios: [],
    scenario: newScenario(label),
    personas: [],
  } as DataModel);

/**
 * Set of default models that can be used to create a new scenario
 *
 * When adding a new model, also provide a translation for them:
 *    MODEL_NAME, MODEL_DESC
 * where the index of the model should match.
 */
export const defaultModels: Array<(language: Languages, emptyBoxLabel: string) => DataModel | Promise<DataModel>> = [
  (_language, emptyBoxLabel) => emptyModel(emptyBoxLabel),
  (language) => loadStarterKit(language, thresholdColors),
];

export type ID = string;

export type User = {
  id: ID;
  name: string;
  phone?: string;
  email?: string;
  url?: string;
  isAuthor?: boolean;
};

export type PageInfo = {
  offsetX: number;
  offsetY: number;
  fontHeight: number;
  line: string;
};

export type EnrichedPageInfo = PageInfo & {
  style: string;
  indented: boolean;
  join: boolean;
  startParagraph: boolean;
  /** Timestamp of the subsequent content blocks */
  timestamp?: number;
};

export type Page = {
  pageNumber: number;
  pageInfo: PageInfo[];
};

export type Log = {
  timestamp?: number;
  author?: string;
  grip?: number;
  blocks: EnrichedPageInfo[];
};

export type TimelineEventType = {
  /** Number representing a JS date */
  timestamp: number;
  /** Index in the logbook that use this timestamp */
  logIndex: number;
  // kind: 'melding' | 'bob' | 'gms' | 'edit';
  // summary?: string;
  author?: string;
};
